import React, { useState } from "react";
import { Box, Collapse, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { SubLineItem } from "../../../types/transactions";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import styles from "./ServiceModifiers.module.css";

interface ServiceModifiersProps {
  subLineItems?: SubLineItem[];
  serviceName?: string;
}

/**
 * Expandable "Service Modifiers (n)" disclosure for a booked service's
 * adjustment-form answers. Collapsed by default; expanded it lists each
 * entry as "Q: {question}" / "A: {answer} (+$xx, +N mins)". Renders nothing
 * when the service has no modifiers.
 */
export default function ServiceModifiers({
  subLineItems,
  serviceName,
}: ServiceModifiersProps) {
  const [opened, setOpened] = useState(false);

  if (!subLineItems || subLineItems.length === 0) {
    return null;
  }

  const formatDeltas = (item: SubLineItem): string | null => {
    const deltas: string[] = [];
    if (item.price_delta && item.price_delta !== 0) {
      deltas.push(formatCurrency(item.price_delta, { showSign: true }));
    }
    if (item.duration_delta_mins && item.duration_delta_mins !== 0) {
      deltas.push(
        `${item.duration_delta_mins > 0 ? "+" : ""}${item.duration_delta_mins} mins`,
      );
    }
    return deltas.length > 0 ? ` (${deltas.join(", ")})` : null;
  };

  return (
    <Box>
      <UnstyledButton
        className={styles.toggle}
        onClick={(e) => {
          // The row this sits in may itself be clickable (e.g. a summary
          // line) — the disclosure toggle must never trigger it.
          e.stopPropagation();
          setOpened((prev) => !prev);
        }}
        aria-expanded={opened}
        aria-label={
          serviceName
            ? `Service Modifiers for ${serviceName}`
            : "Service Modifiers"
        }
      >
        Service Modifiers ({subLineItems.length})
        {opened ? (
          <IconChevronUp size={13} stroke={2.2} />
        ) : (
          <IconChevronDown size={13} stroke={2.2} />
        )}
      </UnstyledButton>
      <Collapse in={opened}>
        <Box className={styles.list}>
          {subLineItems.map((item) => (
            <Box key={item.id}>
              <Box className={styles.question}>Q: {item.question}</Box>
              <Box className={styles.answer}>
                A: {item.answer}
                {formatDeltas(item)}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
